/*
 * WHERE A CHAIN STOPS — and why a report shown without it can be read as something it is not.
 *
 * A hadith collection does not only carry the Prophet's words. The chain of transmission runs back
 * to somebody, and *who that somebody is* is the single most consequential fact about the report:
 *
 *   مرفوع   marfūʿ   the chain reaches the Prophet. His words or his act.
 *   قدسي    qudsī    reaches the Prophet reporting God's speech in his own wording.
 *   موقوف   mawqūf   stops at a COMPANION. This is a Companion's opinion or practice.
 *   مقطوع   maqṭūʿ   stops at a SUCCESSOR — the generation after the Companions.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * THIS LIBRARY CARRIED THE LOWER TWO AND SAID NOTHING, ON 16,919 REPORTS. Measured 2026-08-20
 * across the thirteen shelves taken from hadith-kg — 99,920 reports, which is every row those
 * books hold:
 *
 *   marfūʿ 81,847 · qudsī 1,154   →  the Prophet: 83,001
 *   mawqūf 12,689 · maqṭūʿ 4,230  →  NOT the Prophet: 16,919, or 16.9%
 *
 * The CSVs have a `type` column stating this for every row. `kgRecords` read `no_inbook`, `matn`,
 * `nass` and `hukm` and walked past the column sitting between them, so the distinction was
 * dropped at parse and the corpus has carried unlabelled Companion opinion ever since.
 *
 * It is worst where it is least visible. **Saʿīd b. Manṣūr's Sunan is 2,214 of 2,817 — 78.6% not
 * the Prophet's words**; al-Bayhaqī's Sunan al-Kubrā holds 6,354 and al-Ṭabarānī's Muʿjam al-Kabīr
 * 3,623. A reader meeting one of those met a Companion's ruling laid out exactly like a prophetic
 * hadith, under a collection name they had every reason to trust.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * A LAYER IS NOT A GRADE, and conflating the two is the error this file most has to prevent.
 *
 * `grade` on a `Report` is somebody's verdict on soundness and `reportProblems` refuses it without
 * a `gradedBy`, because a verdict shown bare reads as this library's own. A layer is not a verdict
 * and needs no attributor: it is a structural fact about the chain, stated by the collection, and
 * **a report that stops at a Companion can be perfectly sound**. Marking one mawqūf says where it
 * stops, never that it is weak.
 *
 * `hadith-kg.ts` records the trap from the other side: for 2,560 of al-Ḥākim's rows the `hukm`
 * column is this category copied across, so reading `hukm` as a grade would have told a reader
 * that "stops at a Companion" is a judgement on soundness. `hukmIsGrading` guards that. This
 * file is the other half — having refused to publish the category as a grade, publish it as
 * what it is.
 *
 * AND QUDSĪ IS NOT A LOWER RUNG. It sits beside marfūʿ in the source's own four-valued column and
 * is listed here in that order, but it reaches the Prophet as fully as marfūʿ does. Ordering the
 * four by "closeness to the Prophet" would put qudsī above marfūʿ or below it, and neither is
 * true; the order below is the source's, and `reachesTheProphet` is the only ranking asserted.
 */

/**
 * The four layers, in the order the source's `type` column presents them.
 *
 * THE ONE TABLE. `HadithLayer` is derived from these rows rather than declared beside them, so a
 * layer cannot be added to the type and forgotten in the data or the reverse — `CLAUDE.md`'s rule
 * that a retyped registry is a second copy nothing keeps in step, applied to a list of four where
 * it is just as true as at four hundred.
 *
 * Enumerated rather than detected, for the reason `aramaic.ts` gives: the layer is stated by the
 * collection, and a classifier that misfires puts a false claim about whose words these are onto
 * a page.
 */
const TERMS = [
  { id: 'marfu', arabic: 'مرفوع', translit: 'marfūʿ', reachesTheProphet: true },
  { id: 'qudsi', arabic: 'قدسي', translit: 'qudsī', reachesTheProphet: true },
  { id: 'mawquf', arabic: 'موقوف', translit: 'mawqūf', reachesTheProphet: false },
  { id: 'maqtu', arabic: 'مقطوع', translit: 'maqṭūʿ', reachesTheProphet: false },
] as const;

/** Where the chain stops. The four values the source's own `type` column takes. */
export type HadithLayer = (typeof TERMS)[number]['id'];

/** One layer, as the collections name it. */
export interface LayerTerm {
  readonly id: HadithLayer;
  /** The Arabic term, verbatim as the `type` column writes it. */
  readonly arabic: string;
  /** Transliterated, for a record a reader can search. */
  readonly translit: string;
  /**
   * Whether the chain gets all the way to the Prophet.
   *
   * The ONE ranking this file asserts. Everything else about a layer — whether it is sound,
   * whether it is binding, whether it is worth reading — is somebody's opinion and not ours.
   */
  readonly reachesTheProphet: boolean;
}

/** The four layers, in the source's order. */
export const HADITH_LAYERS: readonly LayerTerm[] = TERMS;

/** Every layer id, derived from the table rather than retyped beside it. */
export const HADITH_LAYER_IDS: readonly HadithLayer[] = TERMS.map(t => t.id);

/** The table row for a layer. */
export function layerTerm(id: HadithLayer): LayerTerm {
  const found = TERMS.find(t => t.id === id);
  /* Unreachable for any `HadithLayer`: the type IS the set of ids in the table it looks in. */
  if (!found) throw new Error(`no such hadith layer: ${id}`);
  return found;
}

/** Whether this layer's chain reaches the Prophet. */
export const reachesTheProphet = (id: HadithLayer): boolean => layerTerm(id).reachesTheProphet;

/**
 * The layer a source's `type` value names, or `undefined` when it names none.
 *
 * Undefined is a real answer and callers must keep it as one: most collections here state no
 * layer at all, and inventing `marfu` for them would claim the Prophet's authority for 194,766
 * reports on the strength of a missing column.
 */
export function layerOf(arabic: string): HadithLayer | undefined {
  const want = arabic.trim();
  return TERMS.find(t => t.arabic === want)?.id;
}

/** Whether a stored value is one of the four ids — for data arriving as a string, from JSON. */
export const isHadithLayer = (value: string): value is HadithLayer =>
  TERMS.some(t => t.id === value);
